import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SignalRService } from './signalR.service';
import { environment } from 'src/environments/environment';
import { CustomReportsHttpClient } from '../../wap-common-process/services/custom-reports-http-header';
import { SignalEvent, SignalRChannel } from '../models/signlaR.model';
import { LoginService } from 'src/app/core/services/login.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { EnvService } from 'src/app/core/deployment/env.service';

@Injectable()
export class PrivateSignalRService extends SignalRService {
  private _signalEvents: Map<string, Subject<SignalEvent<any>>>;
  private _hubConnections: Map<string, HubConnection>;
  private _openConnections: Map<string, boolean>;
  private _initializingConnections: Map<string, boolean>;
  private filterValuesMap: Map<string, SignalRChannel>;
  private attemptsMap: Map<string, number>;
  private _connectionErrors: BehaviorSubject<Map<string, boolean>>;

  constructor(
    private authService: AuthService,
    private loginService: LoginService,
    private env: EnvService
  ) {
    super();
    this._signalEvents = new Map();
    this._hubConnections = new Map();
    this._openConnections = new Map();
    this._initializingConnections = new Map();
    this.filterValuesMap = new Map();
    this.attemptsMap = new Map();
    this._connectionErrors = new BehaviorSubject(new Map());
  }

  // Method to get connection errors
  getConnectionErrors(filterValues: SignalRChannel): Observable<boolean> {
    return this._connectionErrors.asObservable().pipe(
      map((errors) => errors.get(filterValues) ?? false),
      filter((hasError) => hasError)
    );
  }

  // To initialize the connection with endpoint and type
  initializeSignalR(filterValues: SignalRChannel) {
    const key = filterValues;
    this.filterValuesMap.set(key, filterValues);
    this._initializingConnections.set(key, true);
    this.attemptsMap.set(key, 5);
    this._initializeSignalR(key);
  }

  // To subscribe the event
  getDataStream<TDataShape>(filterValues: SignalRChannel): Observable<SignalEvent<TDataShape>> {
    const key = filterValues;
    this._ensureConnection(key);
    if (!this._signalEvents.has(key)) {
      this._signalEvents.set(key, new Subject<SignalEvent<any>>());
    }
    return this._signalEvents
      .get(key)!
      .asObservable()
      .pipe(filter((event) => filterValues === event.type));
  }

  private _ensureConnection(key: string) {
    if (this._openConnections.get(key) || this._initializingConnections.get(key)) return;
    this._initializeSignalR(key);
  }

  private _initializeSignalR(key: string) {
    const filterValues = this.filterValuesMap.get(key);
    if (!filterValues) return;

    let hubConnection: HubConnection;
    const projectId = String(this.loginService.projectTemplate.project.id);
    const templateId = String(this.loginService.projectTemplate.template.id);

    const options: IHttpConnectionOptions = {
      httpClient: new CustomReportsHttpClient(environment.applicationId, projectId, templateId),
      withCredentials: true,
      accessTokenFactory: () => {
        return this.authService.getAuthorizationToken();
      },
    };

    hubConnection = new HubConnectionBuilder().withUrl(this.env.signalRUrl, options).configureLogging(LogLevel.Debug).build();

    this._hubConnections.set(key, hubConnection);

    hubConnection
      .start()
      .then((_) => {
        this._openConnections.set(key, true);
        this._initializingConnections.set(key, false);
        this._setupSignalREvents(key);
      })
      .catch((error) => {
        console.warn(error);
        let attempts = this.attemptsMap.get(key) || 0;
        if (attempts > 0) {
          this.attemptsMap.set(key, attempts - 1);
          setTimeout(() => {
            this._initializeSignalR(key);
          }, 3000);
        } else {
          hubConnection.stop().then((_) => {
            this._openConnections.set(key, false);

            // Report connection error
            const errorMap = new Map(this._connectionErrors.getValue());
            errorMap.set(key, true);
            this._connectionErrors.next(errorMap);
          });
        }
      });
  }

  private _setupSignalREvents(key: string) {
    const filterValues = this.filterValuesMap.get(key);
    if (!filterValues) return;

    const eventOn = filterValues;

    const hubConnection = this._hubConnections.get(key);
    if (!hubConnection) return;

    hubConnection.on(eventOn, (data) => {
      // map or transform your data as appropriate here:
      this._onMessage(key, { type: filterValues, data });
    });
    // To close connection on the setup
    hubConnection.onclose(() => this._openConnections.set(key, false));
  }

  private _onMessage<TDataShape>(key: string, payload: SignalEvent<TDataShape>) {
    const signalEvent = this._signalEvents.get(key);
    if (signalEvent) {
      signalEvent.next(payload);
    }
  }

  stopConnection(filterValues: SignalRChannel) {
    const key = filterValues;
    const hubConnection = this._hubConnections.get(key);
    if (!hubConnection) return;

    const eventOn = filterValues;

    hubConnection.off(eventOn);
    hubConnection.stop().then(() => {
      this._hubConnections.delete(key);
      this._openConnections.delete(key);
      this._initializingConnections.delete(key);
      this.filterValuesMap.delete(key);
      this.attemptsMap.delete(key);
      // Clean up errors when closing the connection manually
      const errorMap = new Map(this._connectionErrors.getValue());
      errorMap.delete(key);
      this._connectionErrors.next(errorMap);

      const signalEvent = this._signalEvents.get(key);
      if (signalEvent) {
        signalEvent.complete();
        this._signalEvents.delete(key);
      }
    });
  }
}
